import { Alert, Button, Dialog, DialogTitle, FormControlLabel, Grid, Switch, TextField } from '@mui/material';
import { AppModels, AppState, UserEmailAttrs, brandNewUserEmail, hasErrors, saveModel, userEmailSelector, userEmailSlice, userEmailValidator, userSelector, userSlice } from 'cdx-common';
import React from 'react';
import { useSelector } from 'react-redux';
import { appStore } from '../../store';

const UserEmailForm = (props: { attrs: UserEmailAttrs, defaultIsDefault: boolean, isDefault: boolean, setIsDefault: (p: boolean) => void, update: (p: Partial<UserEmailAttrs>) => void; }) => {
    return <Grid container padding={2} spacing={2}>
        {props.attrs.__err && <Alert color='error'>{props.attrs.__err}</Alert>}
        <Grid item xs={12}>
            <TextField
                fullWidth
                label='Courriel'
                value={props.attrs.email}
                onChange={(event) => {
                    props.update({ email: event.target.value });
                }}
                error={!!props.attrs.__errors?.email}
                helperText={props.attrs.__errors?.email} />
        </Grid>
        {props.defaultIsDefault === false && <Grid item xs={12}>
            <FormControlLabel control={<Switch checked={props.isDefault} onChange={() => { props.setIsDefault(!props.isDefault); }} />} label='Courriel par défaut' />
        </Grid>}
    </Grid>;
};

export const NewUserEmail = (props: { defaults?: Partial<UserEmailAttrs>, onClose: () => void; }) => {

    const [editingUserEmail, setEditingUserEmail] = React.useState<UserEmailAttrs | undefined>(undefined);

    const user = useSelector((state: AppState) => editingUserEmail ? userSelector.selectById(state, editingUserEmail.userId) : undefined);

    const [isDefault, setIsDefault] = React.useState(false);

    React.useEffect(() => {

        setEditingUserEmail(props.defaults ? { ...brandNewUserEmail, ...props.defaults } : undefined);
    }, [props.defaults]);

    const onSave = async () => {
        if (editingUserEmail) {
            const validatedUserEmail = await userEmailValidator.validate(editingUserEmail);
            if (!hasErrors(validatedUserEmail)) {

                const result = await saveModel(AppModels.userEmails, editingUserEmail);
                setEditingUserEmail(result);
                if (!hasErrors(result)) {
                    appStore.dispatch(userEmailSlice.actions.addOne(result));
                    if (user && isDefault) {
                        const userResult = await saveModel(AppModels.users, {
                            id: user.id,
                            emailId: result.id
                        });
                        appStore.dispatch(userSlice.actions.updateOne({ id: userResult.id, changes: userResult }));
                    }
                    props.onClose();
                }
            }
        }
    };

    if (!editingUserEmail) return <></>;

    const update = (p: Partial<UserEmailAttrs>) => {
        setEditingUserEmail(q => {
            return q ? { ...q, ...p } : undefined;
        }
        );
    };

    return <><Dialog
        open={!!editingUserEmail}
        onClose={props.onClose}>
        <DialogTitle>Nouvelle adresse courriel</DialogTitle>
        <UserEmailForm attrs={editingUserEmail} update={update} defaultIsDefault={false} isDefault={isDefault} setIsDefault={setIsDefault} />
        <Grid container padding={2} spacing={2}>
            <Grid item xs={12}>
                <Button onClick={onSave} color='primary'>Ajouter</Button>
                <Button onClick={props.onClose}>Annuler</Button>
            </Grid>
        </Grid>
    </Dialog></>;
};

export const UserEmailEditor = (props: { id?: ModelId, onClose: () => void; }) => {

    const [editingUserEmail, setEditingUserEmail] = React.useState<UserEmailAttrs | undefined>();
    const userEmail = useSelector((state: AppState) => props.id ? userEmailSelector.selectById(state, props.id) : undefined);
    const user = useSelector((state: AppState) => editingUserEmail ? userSelector.selectById(state, editingUserEmail.userId) : undefined);

    const [updated, setUpdated] = React.useState(false);

    const [isDefault, setIsDefault] = React.useState(false);
    const [defaultIsDefault, setDefaultIsDefault] = React.useState(false);

    React.useEffect(() => {
        setEditingUserEmail(userEmail);
        setIsDefault(!!user && !!userEmail && userEmail.id === user.emailId);
        setDefaultIsDefault(!!user && !!userEmail && userEmail.id === user.emailId);
    }, [userEmail, user]);

    React.useEffect(() => {
        setUpdated(JSON.stringify(editingUserEmail) != JSON.stringify(userEmail) || isDefault !== defaultIsDefault);
    }, [editingUserEmail, userEmail, isDefault]);

    const checkSetDefault = async (userEmailId: ModelId) => {
        if (user && isDefault && user.emailId !== userEmailId) {
            const userResult = await saveModel(AppModels.users, {
                id: user.id,
                emailId: userEmailId
            });
            appStore.dispatch(userSlice.actions.updateOne({ id: userResult.id, changes: userResult }));
        }
    };

    const onSave = async () => {
        if (editingUserEmail) {
            if (updated) {
                if (JSON.stringify(editingUserEmail) != JSON.stringify(userEmail)) {
                    const validatedUserEmail = await userEmailValidator.validate(editingUserEmail);
                    if (!hasErrors(validatedUserEmail)) {
                        const result: UserEmailAttrs = await saveModel(AppModels.userEmails, editingUserEmail);
                        setEditingUserEmail(result);
                        if (!hasErrors(result)) {
                            appStore.dispatch(userEmailSlice.actions.updateOne({ id: editingUserEmail.id, changes: result }));
                            await checkSetDefault(result.id);
                            props.onClose();
                        }

                    }
                } else if (isDefault !== defaultIsDefault) {
                    await checkSetDefault(editingUserEmail.id);
                    props.onClose();
                }
            }
        };
    };

    if (!editingUserEmail) return <></>;

    const update = (p: Partial<UserEmailAttrs>) => {
        setEditingUserEmail(q => {
            return q ? { ...q, ...p } : undefined;
        }
        );
    };

    return <><Dialog
        open={!!userEmail}
        onClose={props.onClose}>
        <DialogTitle>Modifier adresse courriel</DialogTitle>
        <UserEmailForm attrs={editingUserEmail} update={update} defaultIsDefault={defaultIsDefault} isDefault={isDefault} setIsDefault={setIsDefault} />
        <Grid container padding={2} spacing={2}>
            <Grid item xs={12}>
                <Button disabled={!updated} onClick={onSave} color='primary'>Sauvegarder</Button>
                <Button onClick={props.onClose}>Annuler</Button>
            </Grid>
        </Grid>
    </Dialog></>;
};
